import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Wrench, Plus, CheckCircle2, AlertTriangle, Search,
  Package, Shield, Calendar, Building2
} from 'lucide-react';

const ASSET_CATEGORIES = [
  'equipment', 'mechanical', 'electrical', 'plumbing', 'hvac',
  'fire_protection', 'lifts', 'bms', 'security', 'generator',
  'solar', 'water_system', 'landscaping', 'other',
];

const HANDOVER_COLORS = {
  pending: 'bg-amber-100 text-amber-700',
  in_progress: 'bg-blue-100 text-blue-700',
  commissioned: 'bg-purple-100 text-purple-700',
  handed_over: 'bg-green-100 text-green-700',
  defective: 'bg-red-100 text-red-700',
};

export default function CloseoutAssets({ projectId, settings }) {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [filterCat, setFilterCat] = useState('');
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({
    asset_name: '', asset_category: 'equipment', location_text: '',
    serial_no: '', supplier_company: '', installer_company: '',
    commissioning_date: '', warranty_start_date: '', warranty_end_date: '',
    maintenance_interval: '', training_required: false,
  });

  const loadAssets = useCallback(async () => {
    try {
      const res = await api.closeout.listAssets(projectId);
      setAssets(res.data || []);
    } catch { /* silent */ } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { loadAssets(); }, [loadAssets]);

  const handleCreate = async () => {
    if (!form.asset_name.trim()) { toast.error('Asset name required'); return; }
    try {
      await api.closeout.createAsset(projectId, form);
      toast.success('Asset registered');
      setShowCreate(false);
      setForm({ asset_name: '', asset_category: 'equipment', location_text: '', serial_no: '', supplier_company: '', installer_company: '', commissioning_date: '', warranty_start_date: '', warranty_end_date: '', maintenance_interval: '', training_required: false });
      loadAssets();
    } catch { toast.error('Failed to create asset'); }
  };

  const handleStatusUpdate = async (assetId, field, value) => {
    try {
      await api.closeout.updateAsset(assetId, { [field]: value });
      toast.success('Asset updated');
      loadAssets();
    } catch { toast.error('Update failed'); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-slate-300 border-t-slate-800 rounded-full" /></div>;

  const filtered = assets.filter(a => {
    if (filterCat && a.asset_category !== filterCat) return false;
    if (search && !a.asset_name.toLowerCase().includes(search.toLowerCase()) && !(a.asset_code || '').toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const stats = {
    total: assets.length,
    handed_over: assets.filter(a => a.handover_status === 'handed_over').length,
    training_pending: assets.filter(a => a.training_required && !a.training_completed).length,
    warranty_active: assets.filter(a => a.warranty_end_date && a.warranty_end_date > new Date().toISOString().split('T')[0]).length,
  };

  return (
    <div className="space-y-4" data-testid="closeout-assets">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-barlow font-bold uppercase tracking-tight">
          Assets & Systems
          <span className="ml-2 text-sm font-normal text-slate-400">({stats.total} registered)</span>
        </h2>
        <Button size="sm" className="bg-slate-900 hover:bg-slate-800 gap-1" onClick={() => setShowCreate(true)} data-testid="create-asset-btn">
          <Plus className="h-4 w-4" /> Register Asset
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold">{stats.total}</p><p className="text-[10px] text-slate-500 uppercase">Total</p></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-green-600">{stats.handed_over}</p><p className="text-[10px] text-slate-500 uppercase">Handed Over</p></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-amber-600">{stats.training_pending}</p><p className="text-[10px] text-slate-500 uppercase">Training Pending</p></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-blue-600">{stats.warranty_active}</p><p className="text-[10px] text-slate-500 uppercase">Warranty Active</p></CardContent></Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input placeholder="Search assets..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8" data-testid="asset-search" />
        </div>
        <Select value={filterCat} onValueChange={v => setFilterCat(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="All Categories" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {ASSET_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.replace(/_/g, ' ')}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Asset List */}
      {filtered.length === 0 ? (
        <Card><CardContent className="p-12 text-center"><Wrench className="h-12 w-12 text-slate-300 mx-auto mb-3" /><p className="text-lg font-medium text-slate-500">No assets registered yet</p><p className="text-sm text-slate-400">Register equipment, systems, and installations for handover tracking</p></CardContent></Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(asset => (
            <Card key={asset.id} className="hover:shadow-sm transition-shadow" data-testid={`asset-${asset.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className="p-2 rounded-lg bg-slate-100"><Wrench className="h-5 w-5 text-slate-600" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-slate-400">{asset.asset_code}</span>
                      <h3 className="text-sm font-semibold text-slate-800">{asset.asset_name}</h3>
                      <Badge variant="outline" className="text-[10px]">{asset.asset_category?.replace(/_/g, ' ')}</Badge>
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                      {asset.location_text && <span className="flex items-center gap-1"><Package className="h-3 w-3" />{asset.location_text}</span>}
                      {asset.serial_no && <span>S/N: {asset.serial_no}</span>}
                      {asset.supplier_company && <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{asset.supplier_company}</span>}
                      {asset.warranty_end_date && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />Warranty: {asset.warranty_end_date}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={`text-[10px] ${HANDOVER_COLORS[asset.handover_status] || 'bg-slate-100'}`}>
                      {(asset.handover_status || 'pending').replace(/_/g, ' ')}
                    </Badge>
                    {asset.training_required && (
                      <Badge className={`text-[10px] ${asset.training_completed ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                        {asset.training_completed ? 'Trained' : 'Training Required'}
                      </Badge>
                    )}
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    {asset.handover_status !== 'handed_over' && (
                      <Select value="" onValueChange={v => handleStatusUpdate(asset.id, 'handover_status', v)}>
                        <SelectTrigger className="h-7 text-xs w-[130px]"><SelectValue placeholder="Update Status" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="commissioned">Commissioned</SelectItem>
                          <SelectItem value="handed_over">Handed Over</SelectItem>
                          <SelectItem value="defective">Defective</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    {asset.training_required && !asset.training_completed && (
                      <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => handleStatusUpdate(asset.id, 'training_completed', true)}>
                        <CheckCircle2 className="h-3 w-3" /> Mark Trained
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-lg">
          <DialogTitle className="font-barlow font-bold uppercase">Register Asset / System</DialogTitle>
          <div className="space-y-3 mt-2 max-h-[60vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Asset Name *</Label><Input value={form.asset_name} onChange={e => setForm(f => ({...f, asset_name: e.target.value}))} placeholder="e.g. Chiller Unit 1" data-testid="asset-name-input" /></div>
              <div><Label className="text-xs">Category</Label>
                <Select value={form.asset_category} onValueChange={v => setForm(f => ({...f, asset_category: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{ASSET_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Location</Label><Input value={form.location_text} onChange={e => setForm(f => ({...f, location_text: e.target.value}))} placeholder="e.g. Basement Level 2" /></div>
              <div><Label className="text-xs">Serial Number</Label><Input value={form.serial_no} onChange={e => setForm(f => ({...f, serial_no: e.target.value}))} placeholder="S/N" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Supplier</Label><Input value={form.supplier_company} onChange={e => setForm(f => ({...f, supplier_company: e.target.value}))} placeholder="Supplier company" /></div>
              <div><Label className="text-xs">Installer</Label><Input value={form.installer_company} onChange={e => setForm(f => ({...f, installer_company: e.target.value}))} placeholder="Installer company" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Commissioning Date</Label><Input type="date" value={form.commissioning_date} onChange={e => setForm(f => ({...f, commissioning_date: e.target.value}))} /></div>
              <div><Label className="text-xs">Warranty Start</Label><Input type="date" value={form.warranty_start_date} onChange={e => setForm(f => ({...f, warranty_start_date: e.target.value}))} /></div>
              <div><Label className="text-xs">Warranty End</Label><Input type="date" value={form.warranty_end_date} onChange={e => setForm(f => ({...f, warranty_end_date: e.target.value}))} /></div>
            </div>
            <div><Label className="text-xs">Maintenance Interval</Label><Input value={form.maintenance_interval} onChange={e => setForm(f => ({...f, maintenance_interval: e.target.value}))} placeholder="e.g. 6 months, annually" /></div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.training_required} onChange={e => setForm(f => ({...f, training_required: e.target.checked}))} className="rounded" /> Training required for handover</label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button className="bg-slate-900 hover:bg-slate-800" onClick={handleCreate} data-testid="create-asset-submit"><Plus className="h-4 w-4 mr-1" /> Register</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
